import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2, LogOut, Phone, Clock, Truck, Users, ChevronLeft, ChevronRight, ChevronDown, Mail,
  Home, Briefcase, Wallet, User, MapPin, CheckCircle2, ClipboardSignature, CalendarDays, Navigation,
  Play, Square, Timer, Pencil, Crown, MessageSquare, ShieldCheck, Hourglass, Bell,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import Onboarding, { LockScreen } from "@/components/mover/Onboarding";
import { AgreementView } from "@/components/mover/AgreementView";
import { MoverMap } from "@/components/mover/MoverMap";
import { PushEnable } from "@/components/mover/PushEnable";
import { SignaturePad } from "@/components/SignaturePad";
import { FileCheck2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => (n < 0 ? `−$${Math.abs(n)}` : `$${n}`);
axios.defaults.withCredentials = true;

const inputCls =
  "w-full rounded-xl border border-line bg-slate-100/70 px-4 py-3.5 text-base text-slate-900 placeholder-zinc-500 outline-none focus:border-blaze";

const isNew = (job) => {
  if (!job.assigned_at || job.status === "completed") return false;
  return Date.now() - new Date(job.assigned_at).getTime() < 48 * 3600 * 1000;
};

const fmtDate = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

const openDirections = (addr) => {
  const q = encodeURIComponent(addr);
  const isApple = /iPhone|iPad|iPod/.test(navigator.userAgent);
  window.open(
    isApple ? `https://maps.apple.com/?daddr=${q}&dirflg=d` : `https://www.google.com/maps/dir/?api=1&destination=${q}`,
    "_blank"
  );
};

const jobTs = (j) => {
  const base = new Date((j.moving_date || "2100-01-01") + "T00:00:00").getTime();
  const t = Date.parse(`1970-01-01 ${j.start_time || ""}`);
  return base + (isNaN(t) ? 0 : t);
};

const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

const JobTimer = ({ job, onChange }) => {
  const t0 = job.my_timer || { running: false, seconds: 0, started_at: null };
  const [running, setRunning] = useState(!!t0.running);
  const [base, setBase] = useState(t0.seconds || 0);
  const [startedAt, setStartedAt] = useState(t0.started_at ? new Date(t0.started_at).getTime() : null);
  const [, setTick] = useState(0);
  const [editing, setEditing] = useState(false);
  const [hoursInput, setHoursInput] = useState(job.my_logged_hours != null ? String(job.my_logged_hours) : "");
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [sig, setSig] = useState(null);
  const [custName, setCustName] = useState("");

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const elapsed = base + (running && startedAt ? (Date.now() - startedAt) / 1000 : 0);
  const loggedHrs = (elapsed / 3600).toFixed(2);

  const start = () => {
    if (base === 0) { setSignOpen(true); return; }
    doStart(null);
  };

  const doStart = async (signature) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/my/jobs/${job.job_id}/timer/start`, {
        customer_signature: signature || undefined,
        customer_name: custName || undefined,
      });
      setBase(data.seconds || 0);
      setStartedAt(data.started_at ? new Date(data.started_at).getTime() : Date.now());
      setRunning(true);
      setSignOpen(false);
      setSig(null);
      toast.success("Job started — customer notified. The clock is running for the whole crew.");
      if (onChange) onChange();
    } catch (err) {
      if (err.response?.status === 400) { setSignOpen(true); toast.error("Customer signature is required to start the job."); }
      else toast.error("Could not start the timer.");
    } finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/my/jobs/${job.job_id}/timer/stop`);
      setRunning(false);
      setBase((data.hours || 0) * 3600);
      setStartedAt(null);
      setHoursInput(String(data.hours));
      toast.success(`Timer stopped — ${data.hours} hrs added to the whole crew's pay.`);
      if (onChange) onChange();
    } catch { toast.error("Could not stop the timer."); }
    finally { setBusy(false); }
  };

  const saveHours = async () => {
    const h = parseFloat(hoursInput);
    if (!Number.isFinite(h) || h < 0) return toast.error("Enter valid hours.");
    setBusy(true);
    try {
      await axios.post(`${API}/my/jobs/${job.job_id}/hours`, { hours: h });
      setBase(h * 3600);
      setRunning(false);
      setStartedAt(null);
      setEditing(false);
      toast.success("Hours updated.");
      if (onChange) onChange();
    } catch { toast.error("Could not save hours."); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-3 ${running ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`job-timer-${job.job_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${running ? "bg-emerald-600" : "bg-slate-200"}`}>
            <Timer className={`h-5 w-5 ${running ? "text-white" : "text-slate-500"}`} />
          </span>
          <div>
            <p className="font-mono text-xl font-bold tabular-nums text-slate-900" data-testid={`timer-clock-${job.job_id}`}>{fmtClock(elapsed)}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{running ? "Timer running" : base > 0 ? `${loggedHrs} hrs logged` : "Not started"}</p>
            {(job.assigned_movers || []).length > 1 && (
              <p className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-blaze" data-testid={`timer-crew-note-${job.job_id}`}>
                <Users className="h-3 w-3" /> Shared crew timer — starts & stops for everyone
              </p>
            )}
          </div>
        </div>
        {running ? (
          <button onClick={stop} disabled={busy} className="flex items-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50" data-testid={`stop-job-${job.job_id}`}>
            <Square className="h-4 w-4" /> Stop Job
          </button>
        ) : (
          <button onClick={start} disabled={busy} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-xs font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50" data-testid={`start-job-${job.job_id}`}>
            <Play className="h-4 w-4" /> {base > 0 ? "Resume" : "Start Job"}
          </button>
        )}
      </div>
      {!running && base > 0 && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="number" min="0" step="0.01" value={hoursInput}
                onChange={(e) => setHoursInput(e.target.value)}
                className="w-24 rounded-lg border border-line bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blaze"
                data-testid={`timer-hours-input-${job.job_id}`}
              />
              <span className="text-xs text-slate-500">hrs</span>
              <button onClick={saveHours} disabled={busy} className="ml-auto rounded-lg bg-blaze px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white disabled:opacity-50" data-testid={`timer-save-hours-${job.job_id}`}>Save</button>
              <button onClick={() => { setEditing(false); setHoursInput(loggedHrs); }} className="rounded-lg border border-slate-300 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500" data-testid={`timer-cancel-edit-${job.job_id}`}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => { setEditing(true); setHoursInput(loggedHrs); }} className="flex items-center gap-1.5 text-xs font-bold text-blaze" data-testid={`timer-edit-${job.job_id}`}>
              <Pencil className="h-3.5 w-3.5" /> Adjust hours ({loggedHrs} hrs)
            </button>
          )}
        </div>
      )}
      {signOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid={`start-sign-modal-${job.job_id}`}>
          <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="font-display text-lg font-bold text-slate-900">Customer sign to start</h3>
              <p className="text-xs text-slate-500">Ask the customer to sign — this confirms the job is starting and the clock begins now.</p>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">Customer name (optional)</label>
                <input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder={`${job.first_name || ""} ${job.last_name || ""}`.trim()} className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blaze" data-testid={`start-sign-name-${job.job_id}`} />
              </div>
              <SignaturePad label="Customer signature" testid={`start-sign-pad-${job.job_id}`} onChange={setSig} />
              <div className="flex gap-2">
                <button onClick={() => { setSignOpen(false); setSig(null); }} className="flex-1 rounded-xl border border-slate-300 py-3 text-xs font-bold uppercase tracking-wider text-slate-500" data-testid={`start-sign-cancel-${job.job_id}`}>Cancel</button>
                <button onClick={() => doStart(sig)} disabled={busy || !sig} className="flex flex-[2] items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-xs font-bold uppercase tracking-wider text-white active:scale-95 disabled:opacity-50" data-testid={`start-sign-confirm-${job.job_id}`}>
                  <Play className="h-4 w-4" /> Confirm & Start
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const JobCard = ({ job, selfName, rate, embedded, onStatus }) => {
  const [showDir, setShowDir] = useState(false);
  const [crewStatus, setCrewStatus] = useState(((job.crew_status || {})[selfName] || {}).status || "");
  const crew = (job.assigned_movers || []).filter((m) => m !== selfName);
  const done = job.status === "completed";
  const partner = (job.source === "movers_buddy") || (job.job_id || "").startsWith("MB-");
  const pickupAddr = [job.pickup_address, job.pickup_suburb].filter(Boolean).join(", ");
  const dropAddr = [job.drop_address, job.drop_suburb].filter(Boolean).join(", ");
  const moveType = job.bedrooms ? `${job.bedrooms} Bedroom Move` : (job.property_type ? `${job.property_type} Move` : "Move");
  const crewCount = (job.assigned_movers || []).length || 1;
  const custInitial = (job.first_name || job.email || "C").charAt(0).toUpperCase();
  const running = (job.my_timer || {}).running;

  const sendLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        axios.post(`${API}/my/jobs/${job.job_id}/location`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  useEffect(() => {
    if (done || crewStatus !== "on_my_way") return;
    sendLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewStatus, done]);

  const sendStatus = async (s) => {
    try {
      await axios.post(`${API}/my/jobs/${job.job_id}/status`, { status: s });
      setCrewStatus(s);
      sendLocation();
      if (onStatus) onStatus();
      toast.success(s === "on_my_way" ? "Office notified — sharing your location while you drive." : "Office notified — you've arrived.");
    } catch {
      toast.error("Could not update status.");
    }
  };
  return (
    <div className={`relative overflow-hidden bg-white before:absolute before:bottom-4 before:left-0 before:top-4 before:w-0.5 before:rounded-r-full ${partner ? "before:bg-slate-400" : "before:bg-amber-500"} ${embedded ? "" : `rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-200 ease-out ${partner ? "border-slate-200/80" : "border-amber-200 ring-1 ring-amber-100"}`}`} data-testid={`mover-job-${job.job_id}`}>
      <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-5">
        <div className="min-w-0">
          <p className="font-display text-xl font-bold tracking-tight text-slate-900" data-testid={`job-title-${job.job_id}`}>
            Job #{job.job_id}
            {isNew(job) && <span className="ml-2 inline-block rounded-full bg-blaze px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white align-middle" data-testid={`new-badge-${job.job_id}`}>New</span>}
          </p>
          <p className="mt-0.5 truncate text-sm font-medium text-slate-500" data-testid={`job-movetype-${job.job_id}`}>{moveType}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${done ? "bg-green-600 text-white" : running ? "bg-blaze text-white" : crewStatus === "arrived" ? "bg-green-100 text-green-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`job-status-${job.job_id}`}>
            {done ? "Completed" : running ? "In Progress" : crewStatus === "arrived" ? "On Site" : "Accepted"}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${partner ? "bg-slate-200 text-slate-600" : "bg-gradient-to-r from-amber-500 to-yellow-400 text-white"}`} data-testid={`job-source-${job.job_id}`}>
            {partner ? "Movers Buddy" : <><Crown className="h-3 w-3" /> MoversNearby</>}
          </span>
        </div>
      </div>
      <div className="space-y-4 px-5 pb-5">
        <div className="rounded-2xl border border-slate-200 p-4" data-testid={`customer-card-${job.job_id}`}>
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 text-lg font-bold text-blaze">{custInitial}</span>
            <div className="min-w-0">
              <p className="truncate text-base font-bold text-slate-900">{job.first_name} {job.last_name}</p>
              <p className="truncate text-xs text-slate-500">Customer{job.mobile ? ` · ${job.mobile}` : ""}</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <a href={job.mobile ? `tel:${job.mobile}` : undefined} className={`flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 py-3 text-xs font-bold text-slate-800 active:scale-95 ${job.mobile ? "" : "pointer-events-none opacity-40"}`} data-testid={`cust-call-${job.job_id}`}>
              <Phone className="h-5 w-5 text-red-500" /> Call
            </a>
            <a href={job.mobile ? `sms:${job.mobile}` : undefined} className={`flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 py-3 text-xs font-bold text-slate-800 active:scale-95 ${job.mobile ? "" : "pointer-events-none opacity-40"}`} data-testid={`cust-message-${job.job_id}`}>
              <MessageSquare className="h-5 w-5 text-slate-700" /> Message
            </a>
            <a href={job.email ? `mailto:${job.email}` : undefined} className={`flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 py-3 text-xs font-bold text-slate-800 active:scale-95 ${job.email ? "" : "pointer-events-none opacity-40"}`} data-testid={`cust-email-${job.job_id}`}>
              <Mail className="h-5 w-5 text-slate-700" /> Email
            </a>
          </div>
          <p className="mt-3 flex items-center gap-2 text-xs font-medium text-slate-500">
            <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" /> Keep contact to this job — dispatch handles everything else
          </p>
        </div>
        {pickupAddr && dropAddr && (
          <img
            src={`${API}/geo/route-map?from=${encodeURIComponent(pickupAddr)}&to=${encodeURIComponent(dropAddr)}`}
            alt="Route map"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="block w-full rounded-2xl border border-slate-200 bg-slate-100"
            style={{ aspectRatio: "640 / 320", objectFit: "cover" }}
            data-testid={`route-map-${job.job_id}`}
          />
        )}
        <div className="rounded-2xl border border-slate-200 p-4" data-testid={`route-${job.job_id}`}>
          <div className="flex gap-3">
            <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-green-600" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Pick up</p>
              <p className="text-sm font-semibold text-slate-900">{pickupAddr || "—"}</p>
            </div>
          </div>
          <div className="ml-[5px] h-4 w-px bg-slate-200" />
          <div className="flex gap-3">
            <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full bg-red-500" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Drop off</p>
              <p className="text-sm font-semibold text-slate-900">{dropAddr || "—"}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-1 text-sm font-medium text-slate-600" data-testid={`job-stats-${job.job_id}`}>
          {job.travel_km ? <span className="inline-flex items-center gap-1.5"><Navigation className="h-4 w-4 text-slate-400" />{job.travel_km} km</span> : null}
          {job.travel_minutes ? <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" />{job.travel_minutes} min drive</span> : null}
          <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-400" />Crew {crewCount}</span>
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-slate-400" />{fmtDate(job.moving_date)}{job.start_time ? ` · ${job.start_time}` : ""}</span>
        </div>
        {(job.crew_contacts && job.crew_contacts.length) ? (
          <div className="space-y-1.5" data-testid={`crew-list-${job.job_id}`}>
            {job.crew_contacts.map((c) => (
              <div key={c.name} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs" data-testid={`crew-${job.job_id}-${c.name.toLowerCase()}`}>
                <span className="font-semibold text-slate-700"><Users className="mr-1.5 inline h-3.5 w-3.5 text-blaze" />With {c.name}</span>
                {c.phone ? <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 rounded-full bg-green-600 px-3 py-1.5 font-bold text-white active:scale-95" data-testid={`crew-call-${job.job_id}-${c.name.toLowerCase()}`}><Phone className="h-3 w-3" /> {c.phone}</a> : null}
              </div>
            ))}
          </div>
        ) : null}
        {(pickupAddr || dropAddr) && (
          <button onClick={() => setShowDir((s) => !s)} className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-slate-900 py-4 text-sm font-bold text-slate-900 active:scale-[0.98]" data-testid={`directions-btn-${job.job_id}`}>
            <Navigation className="h-4 w-4" /> Get directions
          </button>
        )}
        {showDir && (
          <div className="grid grid-cols-2 gap-2 rounded-xl border border-blue-200 bg-blue-50 p-2" data-testid={`directions-popup-${job.job_id}`}>
            <button
              onClick={() => pickupAddr && openDirections(pickupAddr)}
              disabled={!pickupAddr}
              className="flex flex-col items-center gap-1 rounded-lg bg-white py-3 text-xs font-bold text-slate-800 shadow-sm active:scale-95 disabled:opacity-40"
              data-testid={`dir-pickup-${job.job_id}`}
            >
              <MapPin className="h-4 w-4 text-green-600" />
              Pickup
              <span className="px-1 text-[10px] font-medium normal-case leading-tight text-slate-500">{pickupAddr || "No address"}</span>
            </button>
            <button
              onClick={() => dropAddr && openDirections(dropAddr)}
              disabled={!dropAddr}
              className="flex flex-col items-center gap-1 rounded-lg bg-white py-3 text-xs font-bold text-slate-800 shadow-sm active:scale-95 disabled:opacity-40"
              data-testid={`dir-dropoff-${job.job_id}`}
            >
              <MapPin className="h-4 w-4 text-red-500" />
              Drop-off
              <span className="px-1 text-[10px] font-medium normal-case leading-tight text-slate-500">{dropAddr || "No address"}</span>
            </button>
          </div>
        )}
        {job.my_pay != null ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-3" data-testid={`job-pay-${job.job_id}`}>
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-green-700/80">
              <span className="rounded-md bg-white px-2 py-0.5 font-mono text-[11px] font-bold text-green-700 shadow-sm" data-testid={`job-pay-id-${job.job_id}`}>{job.job_id}</span>
              <span data-testid={`job-pay-date-${job.job_id}`}>{fmtDate(job.moving_date)}</span>
            </div>
            <p className="mt-1.5 text-sm font-semibold text-green-700">
              Your pay: ({job.my_hours} work{job.my_travel ? ` + ${job.my_travel} travel` : ""}) hrs × ${job.my_rate ?? rate}/hr{job.my_callout ? ` + $${job.my_callout} call-out` : ""}{job.my_extra ? ` + $${job.my_extra} extra` : ""} = ${job.my_pay}
            </p>
            {job.my_extra && job.my_extra_note ? (
              <p className="mt-1 text-xs font-semibold text-green-700/80" data-testid={`job-pay-extra-note-${job.job_id}`}>Extra: {job.my_extra_note}</p>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-slate-500" data-testid={`job-rate-${job.job_id}`}>Your rate: ${rate}/hr — pay appears here after sign-off.</p>
        )}
        {job.notes && (
          <p className="rounded-xl border border-yellow-200 bg-yellow-50 p-3 text-xs leading-relaxed text-yellow-800">
            Special instructions: {job.notes}
          </p>
        )}
        {!done && (
          <>
            <JobTimer job={job} onChange={onStatus} />
            <div className="grid grid-cols-2 gap-2" data-testid={`crew-status-${job.job_id}`}>
              <button
                onClick={() => sendStatus("on_my_way")}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-xs font-bold uppercase tracking-wider transition-colors active:scale-[0.98] ${crewStatus === "on_my_way" ? "border-amber-500 bg-amber-500 text-white" : "border-amber-400 bg-white text-amber-600"}`}
                data-testid={`status-onmyway-${job.job_id}`}
              >
                <Navigation className="h-4 w-4" /> {crewStatus === "on_my_way" ? "On My Way ✓" : "On My Way"}
              </button>
              <button
                onClick={() => sendStatus("arrived")}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-xs font-bold uppercase tracking-wider transition-colors active:scale-[0.98] ${crewStatus === "arrived" ? "border-green-600 bg-green-600 text-white" : "border-green-500 bg-white text-green-600"}`}
                data-testid={`status-arrived-${job.job_id}`}
              >
                <CheckCircle2 className="h-4 w-4" /> {crewStatus === "arrived" ? "Arrived ✓" : "Arrived"}
              </button>
            </div>
            <a
              href={`/signoff?job=${job.job_id}`}
              className="block rounded-xl bg-blaze py-4 text-center text-sm font-bold uppercase tracking-[0.15em] text-white transition-transform active:scale-[0.98]"
              data-testid={`signoff-btn-${job.job_id}`}
            >
              Sign Off Job
            </a>
          </>
        )}
      </div>
    </div>
  );
};

const CollapsibleJob = ({ job, selfName, rate, defaultOpen = false, onStatus }) => {
  const [open, setOpen] = useState(defaultOpen);
  const done = job.status === "completed";
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid={`job-collapsed-${job.job_id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-slate-50"
        data-testid={`job-toggle-${job.job_id}`}
      >
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${done ? "bg-green-50" : "bg-blue-50"}`}>
          <Clock className={`h-5 w-5 ${done ? "text-green-600" : "text-blaze"}`} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold text-blaze">{job.job_id}</span>
            {isNew(job) && <span className="rounded-full bg-blaze px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">New</span>}
            {done && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-green-700">Done</span>}
          </span>
          <span className="mt-0.5 block truncate text-xs font-semibold text-slate-600" data-testid={`job-datetime-${job.job_id}`}>
            {fmtDate(job.moving_date)}{job.start_time ? ` · ${job.start_time}` : ""}
          </span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100" data-testid={`job-expanded-${job.job_id}`}>
          <JobCard job={job} selfName={selfName} rate={rate} embedded onStatus={onStatus} />
        </div>
      )}
    </div>
  );
};

const MiniCalendar = ({ jobs, selected, onSelect }) => {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const counts = useMemo(() => {
    const c = {};
    jobs.forEach((j) => {
      if (j.moving_date) c[j.moving_date] = (c[j.moving_date] || 0) + 1;
    });
    return c;
  }, [jobs]);
  const y = month.getFullYear();
  const m = month.getMonth();
  const daysIn = new Date(y, m + 1, 0).getDate();
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  const todayStr = new Date().toLocaleDateString("en-CA");
  const dstr = (day) => `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return (
    <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="mover-mini-calendar">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blaze">
          <CalendarDays className="h-4 w-4" /> Job Calendar
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth(new Date(y, m - 1, 1))} className="rounded-lg p-1.5 text-slate-500 active:bg-slate-100" data-testid="cal-prev-month"><ChevronLeft className="h-4 w-4" /></button>
          <p className="w-28 text-center text-sm font-bold text-slate-900" data-testid="cal-month-label">
            {month.toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
          </p>
          <button onClick={() => setMonth(new Date(y, m + 1, 1))} className="rounded-lg p-1.5 text-slate-500 active:bg-slate-100" data-testid="cal-next-month"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <span key={d + i} className="py-1 text-[10px] font-bold uppercase text-slate-400">{d}</span>
        ))}
        {Array(offset).fill(null).map((_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: daysIn }, (_, i) => i + 1).map((day) => {
          const ds = dstr(day);
          const count = counts[ds] || 0;
          const isSel = selected === ds;
          const isToday = ds === todayStr;
          return (
            <button
              key={day}
              onClick={() => count && onSelect(isSel ? null : ds)}
              className={`relative flex h-11 flex-col items-center justify-center rounded-xl text-sm transition-all ${
                isSel ? "bg-blaze font-bold text-white shadow-md shadow-blue-500/30"
                : count ? "bg-blue-50 font-bold text-blaze active:scale-95"
                : "text-slate-400"
              } ${isToday && !isSel ? "ring-2 ring-blaze/40" : ""}`}
              data-testid={`cal-day-${ds}`}
            >
              {day}
              {count > 0 && (
                <span className={`absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-0.5 text-[9px] font-bold ${isSel ? "bg-white text-blaze" : "bg-blaze text-white"}`} data-testid={`cal-count-${ds}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tap a highlighted date to see that day's jobs</p>
    </div>
  );
};

const Payslip = () => {
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [slip, setSlip] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/my/payslip`, { params: { date } }).then(({ data }) => setSlip(data)).catch(() => setSlip(null));
  }, [date]);

  const shift = (days) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + days);
    setDate(d.toLocaleDateString("en-CA"));
  };

  const emailSlip = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/my/payslip/email`, null, { params: { date } });
      toast.success("Payslip sent to your email.");
    } catch {
      toast.error("Could not send payslip.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="payslip-section">
      <div className="rounded-2xl border border-line bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <button onClick={() => shift(-7)} className="rounded-full border border-line p-2.5 text-slate-600 active:scale-95" data-testid="week-prev"><ChevronLeft className="h-5 w-5" /></button>
          <span className="text-sm font-semibold text-slate-800" data-testid="payslip-week">
            {slip ? `${slip.week_start} → ${slip.week_end}` : "…"}
          </span>
          <button onClick={() => shift(7)} className="rounded-full border border-line p-2.5 text-slate-600 active:scale-95" data-testid="week-next"><ChevronRight className="h-5 w-5" /></button>
        </div>
        {slip && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="payslip-status">
            {slip.paid ? (
              <span className="rounded-full bg-green-100 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-green-700" data-testid="payslip-paid-badge">
                Paid{slip.paid_at ? ` · ${slip.paid_at}` : ""}
              </span>
            ) : (
              <span className="rounded-full bg-yellow-100 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-yellow-700" data-testid="payslip-unpaid-badge">Unpaid</span>
            )}
            {!slip.paid && slip.pay_date && (
              <span className="text-xs text-slate-600" data-testid="payslip-pay-date">Payment on <strong>{slip.pay_date}</strong></span>
            )}
          </div>
        )}
      </div>

      {slip && (
        <>
          <div className="mt-4 relative overflow-hidden rounded-3xl bg-blaze p-5 text-white shadow-[0_12px_40px_rgba(37,99,235,0.35)]" data-testid="payslip-total">
            <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/15 blur-2xl" />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-100">This Week's Pay</p>
            <p className="mt-1 font-display text-4xl font-bold">{fmtMoney(slip.total_pay)}</p>
            <p className="mt-1 text-xs text-blue-100">
              {slip.total_hours} hrs × ${slip.rate_label || slip.rate}/hr{slip.total_callout ? ` + $${slip.total_callout} call-out` : ""}{slip.total_extras ? ` + $${slip.total_extras} extras` : ""}{slip.total_adjustments ? ` ${slip.total_adjustments > 0 ? "+" : "−"}$${Math.abs(slip.total_adjustments)} adjustments` : ""}
            </p>
          </div>

          <div className="mt-4 rounded-2xl border border-line bg-white p-4 shadow-sm">
            {slip.jobs.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500" data-testid="payslip-empty">No completed jobs with logged hours this week.</p>
            ) : (
              <div className="divide-y divide-line/60" data-testid="payslip-table">
                {slip.jobs.map((j, i) => (
                  <div key={j.job_id + i} className="flex items-center justify-between gap-3 py-3" data-testid={`payslip-row-${j.job_id || j.kind}-${i}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {j.kind === "extra" ? (
                          <span className="text-green-700">Extra{j.note ? ` — ${j.note}` : ""}</span>
                        ) : j.kind === "deduction" ? (
                          <span className="text-red-600">Deduction{j.note ? ` — ${j.note}` : ""}</span>
                        ) : (
                          <>
                            <span className="rounded-md bg-blue-50 px-2 py-0.5 font-mono text-xs font-bold text-blaze" data-testid={`payslip-jobid-${j.job_id}`}>{j.job_id}</span>
                            {j.manual && <span className="ml-2 rounded border border-yellow-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-yellow-700">Manual</span>}
                            {j.partner && <span className="ml-2 rounded border border-sky-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-700">Partner</span>}
                          </>
                        )}
                      </p>
                      <p className="mt-1 text-xs font-semibold text-slate-600" data-testid={`payslip-date-${j.job_id || j.kind}-${i}`}>
                        {fmtDate(j.date)}{j.hours ? ` · ${j.hours} hrs work` : ""}{j.travel ? ` + ${j.travel} hrs travel` : ""}{j.callout ? ` · $${j.callout} call-out` : ""}
                      </p>
                      {j.extra ? (
                        <p className="mt-1 text-xs font-bold text-green-700" data-testid={`payslip-extra-${j.job_id}-${i}`}>
                          + ${j.extra} extra{j.extra_note ? ` — ${j.extra_note}` : ""}
                        </p>
                      ) : null}
                    </div>
                    <p className={`shrink-0 text-sm font-bold ${j.kind === "deduction" ? "text-red-600" : j.kind === "extra" ? "text-green-700" : "text-slate-900"}`}>
                      {j.kind === "deduction" ? `-$${Math.abs(j.pay)}` : j.kind === "extra" ? `+$${j.pay}` : `$${j.pay}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {slip.jobs.some((j) => j.manual) && (
              <p className="mt-2 border-t border-line pt-3 text-xs text-slate-500" data-testid="manual-readonly-note">
                Manual entries are added by the office and are view-only.
              </p>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              onClick={emailSlip}
              disabled={sending || slip.jobs.length === 0}
              className="flex items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-xs font-bold uppercase tracking-widest text-white active:scale-[0.98] disabled:opacity-50"
              data-testid="email-payslip-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email Payslip
            </button>
            <a
              href={`${API}/my/payslip/pdf?date=${date}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 rounded-xl border-2 border-blaze py-4 text-xs font-bold uppercase tracking-widest text-blaze active:scale-[0.98]"
              data-testid="download-payslip-btn"
            >
              Download PDF
            </a>
          </div>
        </>
      )}
    </div>
  );
};

const EARN_PERIODS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "Week"], ["month", "Month"], ["range", "Range"]];
const fmtDay = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }).toUpperCase(); } catch { return d; } };
const shiftWeek = (ref, days) => { const d = new Date(ref + "T00:00:00"); d.setDate(d.getDate() + days); return d.toLocaleDateString("en-CA"); };
const shiftMonth = (ym, delta) => { const [y, m] = ym.split("-").map(Number); const d = new Date(y, m - 1 + delta, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const Earnings = ({ user }) => {
  const [period, setPeriod] = useState("month");
  const [weekRef, setWeekRef] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [monthRef, setMonthRef] = useState(() => new Date().toLocaleDateString("en-CA").slice(0, 7));
  const [rangeMonths, setRangeMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const slipParams = useMemo(() => {
    const p = { period };
    if (period === "week") p.ref = weekRef;
    if (period === "month") p.month = monthRef;
    if (period === "range") p.months = rangeMonths;
    return p;
  }, [period, weekRef, monthRef, rangeMonths]);
  const slipQuery = useMemo(() => new URLSearchParams(slipParams).toString(), [slipParams]);

  useEffect(() => {
    setLoading(true);
    const params = { period };
    if (period === "week") params.ref = weekRef;
    if (period === "month") params.month = monthRef;
    if (period === "range") params.months = rangeMonths;
    axios.get(`${API}/my/earnings`, { params })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, weekRef, monthRef, rangeMonths]);

  const emailSlip = async () => {
    setSending(true);
    try { await axios.post(`${API}/my/payslip/email`, null, { params: slipParams }); toast.success("Payslip for this period sent to your email."); }
    catch { toast.error("Could not send payslip."); }
    finally { setSending(false); }
  };

  const groups = useMemo(() => {
    if (!data) return [];
    const map = {};
    (data.jobs || []).forEach((j) => { (map[j.date] = map[j.date] || []).push(j); });
    return Object.keys(map).sort((a, b) => (a < b ? 1 : -1)).map((d) => ({ date: d, jobs: map[d], total: map[d].reduce((s, j) => s + j.pay, 0) }));
  }, [data]);

  const initials = (user.name || "").split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "ME";
  const maxWeek = data ? Math.max(...data.weeks.map((w) => w.total), 1) : 1;

  return (
    <div data-testid="mover-earnings-screen">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blaze/10 text-sm font-bold text-blaze" data-testid="earn-avatar">{initials}</span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">Earnings</p>
            <p className="font-display text-xl font-bold text-slate-900" data-testid="earn-jobcount">{data ? data.job_count : 0} jobs</p>
          </div>
        </div>
        <Bell className="h-6 w-6 text-slate-400" />
      </div>

      <div className="mt-4 grid grid-cols-5 gap-1.5" data-testid="earn-period-tabs">
        {EARN_PERIODS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setPeriod(key)}
            className={`rounded-xl py-3 text-[11px] font-bold transition-all active:scale-95 ${period === key ? "bg-slate-900 text-white shadow-md" : "bg-white text-slate-500 shadow-sm"}`}
            data-testid={`earn-period-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {period === "week" && (
        <div className="mt-3 flex items-center justify-between rounded-2xl bg-white px-2 py-2 shadow-sm" data-testid="earn-week-nav">
          <button onClick={() => setWeekRef((r) => shiftWeek(r, -7))} className="rounded-full p-2 text-slate-600 active:scale-90" data-testid="earn-week-prev"><ChevronLeft className="h-5 w-5" /></button>
          <span className="text-xs font-bold text-slate-800" data-testid="earn-week-label">{data ? data.label : "…"}</span>
          <button onClick={() => setWeekRef((r) => shiftWeek(r, 7))} disabled={data && data.is_current} className="rounded-full p-2 text-slate-600 active:scale-90 disabled:opacity-30" data-testid="earn-week-next"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}
      {period === "month" && (
        <div className="mt-3 flex items-center justify-between rounded-2xl bg-white px-2 py-2 shadow-sm" data-testid="earn-month-nav">
          <button onClick={() => setMonthRef((r) => shiftMonth(r, -1))} className="rounded-full p-2 text-slate-600 active:scale-90" data-testid="earn-month-prev"><ChevronLeft className="h-5 w-5" /></button>
          <span className="text-xs font-bold text-slate-800" data-testid="earn-month-label">{data ? data.label : "…"}</span>
          <button onClick={() => setMonthRef((r) => shiftMonth(r, 1))} disabled={data && data.is_current} className="rounded-full p-2 text-slate-600 active:scale-90 disabled:opacity-30" data-testid="earn-month-next"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}
      {period === "range" && (
        <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="earn-range-nav">
          {[2, 3, 6].map((n) => (
            <button key={n} onClick={() => setRangeMonths(n)} className={`rounded-xl py-2.5 text-[11px] font-bold transition-all active:scale-95 ${rangeMonths === n ? "bg-blaze text-white shadow-md" : "bg-white text-slate-500 shadow-sm"}`} data-testid={`earn-range-${n}`}>Last {n} months</button>
          ))}
        </div>
      )}

      {loading && !data ? (
        <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>
      ) : data ? (
        <>
          <div className="relative mt-4 overflow-hidden rounded-3xl bg-slate-900 p-5 text-white shadow-[0_12px_40px_rgba(15,23,42,0.35)]" data-testid="earn-summary-card">
            <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-blaze/20 blur-3xl" />
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{data.label}</p>
            <p className="mt-1 font-display text-4xl font-bold tracking-tight" data-testid="earn-total">{fmtMoney(data.total)}</p>
            <div className="mt-4 flex h-16 items-end gap-2.5">
              {data.weeks.map((w, i) => (
                <div
                  key={i}
                  title={`${w.label}: ${fmtMoney(w.total)}`}
                  className={`flex-1 rounded-lg ${w.current ? "bg-blaze" : "bg-white/15"}`}
                  style={{ height: `${Math.max((w.total / maxWeek) * 64, 5)}px` }}
                  data-testid={`earn-bar-${i}`}
                />
              ))}
            </div>
            <div className="mt-2.5 flex items-center justify-between text-[11px]">
              <span className="font-medium text-slate-400">{data.weeks[0] ? data.weeks[0].label : ""}</span>
              <span className="font-bold text-blaze" data-testid="earn-thisweek">{data.is_current ? "this week" : "latest wk"} {fmtMoney(data.this_week)}</span>
            </div>
          </div>

          {data.unpaid_count > 0 && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl bg-red-50 px-5 py-4" data-testid="earn-unpaid-banner">
              <Hourglass className="h-6 w-6 shrink-0 text-red-500" />
              <p className="text-base font-bold text-red-600">{fmtMoney(data.unpaid_total)} unpaid<span className="ml-2 text-sm font-semibold text-red-400">{data.unpaid_count} job{data.unpaid_count === 1 ? "" : "s"}</span></p>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="earn-avg-card">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Avg per job</p>
              <p className="mt-1 font-display text-2xl font-bold text-slate-900">{fmtMoney(data.avg_per_job)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="earn-paidout-card">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Paid out</p>
              <p className="mt-1 font-display text-2xl font-bold text-green-600">{fmtMoney(data.paid_out)}</p>
            </div>
          </div>

          <h2 className="mb-1 mt-6 font-display text-lg font-bold text-slate-900">Jobs by moving date</h2>
          {groups.length === 0 ? (
            <p className="mt-3 rounded-2xl border border-dashed border-line bg-white/60 p-6 text-center text-sm text-slate-500" data-testid="earn-empty">No earnings in this period.</p>
          ) : (
            <div className="mt-2 space-y-3" data-testid="earn-jobs-list">
              {groups.map((g) => (
                <div key={g.date} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-1.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500" data-testid={`earn-group-date-${g.date}`}>{fmtDay(g.date)}</span>
                    <span className="text-xs font-semibold text-slate-500">{fmtMoney(g.total)} · {g.jobs.length} job{g.jobs.length === 1 ? "" : "s"}</span>
                  </div>
                  {g.jobs.map((j, i) => (
                    <div key={j.job_id + i} className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3" data-testid={`earn-job-${j.job_id}`}>
                      <div className="min-w-0">
                        <p className="font-display text-base font-bold text-slate-900">{j.adjustment ? (j.kind === "deduction" ? "Deduction" : "Add-on") : `Job #${j.job_id}`}</p>
                        <p className="truncate text-xs text-slate-500">
                          {j.customer}{j.move_type ? ` · ${j.move_type}` : ""}
                          {!j.adjustment && j.hours != null ? ` · ${j.hours}${j.travel ? ` + ${j.travel} travel` : ""} hrs` : ""}
                        </p>
                        {!j.adjustment && j.extra ? (
                          <p className="text-[11px] font-semibold text-green-700" data-testid={`earn-extra-${j.job_id}`}>+{fmtMoney(j.extra)} heavy lifting / stairs{j.extra_note ? ` · ${j.extra_note}` : ""}</p>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={`font-display text-base font-bold ${j.kind === "deduction" ? "text-red-600" : j.kind === "extra" ? "text-green-700" : "text-slate-900"}`}>
                          {j.kind === "deduction" ? `−${fmtMoney(Math.abs(j.pay))}` : j.kind === "extra" ? `+${fmtMoney(j.pay)}` : fmtMoney(j.pay)}
                        </p>
                        <span className={`mt-0.5 inline-block rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${j.paid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-500"}`} data-testid={`earn-badge-${j.job_id}`}>{j.paid ? "Paid" : "Unpaid"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button onClick={emailSlip} disabled={sending} className="flex items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-xs font-bold uppercase tracking-widest text-white active:scale-[0.98] disabled:opacity-50" data-testid="email-payslip-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email Payslip
            </button>
            <a href={`${API}/my/payslip/pdf?${slipQuery}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl border-2 border-blaze py-4 text-xs font-bold uppercase tracking-widest text-blaze active:scale-[0.98]" data-testid="download-payslip-btn">
              Download PDF
            </a>
          </div>
        </>
      ) : (
        <p className="mt-8 text-center text-sm text-slate-500">Could not load earnings.</p>
      )}
    </div>
  );
};


const WeekPayCard = ({ onOpen }) => {
  const [slip, setSlip] = useState(null);
  const date = new Date().toLocaleDateString("en-CA");
  useEffect(() => {
    axios.get(`${API}/my/payslip`, { params: { date } }).then(({ data }) => setSlip(data)).catch(() => setSlip(null));
  }, [date]);
  if (!slip) return null;
  return (
    <button onClick={onOpen} className="mt-4 flex w-full items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-white px-4 py-3 text-left shadow-sm active:scale-[0.99]" data-testid="week-pay-card">
      <span className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50"><Wallet className="h-4 w-4 text-blaze" /></span>
        <span>
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">This week's pay</span>
          <span className="block font-display text-lg font-bold text-slate-900" data-testid="week-pay-total">{fmtMoney(slip.total_pay)}</span>
        </span>
      </span>
      <span className="flex items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest ${slip.paid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`} data-testid="week-pay-status">{slip.paid ? "Paid" : "Unpaid"}</span>
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </span>
    </button>
  );
};

const NAV = [
  { key: "today", label: "Today", icon: Home },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "mapview", label: "Map", icon: MapPin },
  { key: "earnings", label: "Earnings", icon: Wallet },
  { key: "profile", label: "Profile", icon: User },
];

export default function Mover() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [nav, setNav] = useState("today");
  const [jobsView, setJobsView] = useState("upcoming");
  const [selectedDate, setSelectedDate] = useState(null);
  const [onboard, setOnboard] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);

  const loadJobs = () => axios.get(`${API}/my/jobs`).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  const loadStatus = () =>
    axios.get(`${API}/onboarding/me`).then(({ data }) => {
      setOnboard(data);
      if (data.status !== "locked") loadJobs();
      return data;
    }).catch(() => setOnboard({ status: "active", prefill: {}, profile: {} }));

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(({ data }) => {
        if (["mover", "admin"].includes(data.role)) {
          setUser(data);
          loadStatus();
        } else if (data.role === "contractor") {
          window.location.href = "/admin";
        } else setUser(false);
      })
      .catch(() => { window.location.href = "/login"; })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !navigator.geolocation) return;
    const active = jobs.filter(
      (j) => j.status !== "completed" && ((j.crew_status || {})[user.name] || {}).status === "on_my_way"
    );
    if (active.length === 0) return;

    let lastSent = 0;
    const post = (pos, force = false) => {
      const now = Date.now();
      if (!force && now - lastSent < 15000) return;
      lastSent = now;
      active.forEach((j) =>
        axios.post(`${API}/my/jobs/${j.job_id}/location`, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        }).catch(() => {})
      );
    };

    const watchId = navigator.geolocation.watchPosition(
      (pos) => post(pos),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000 }
    );

    const pushNow = () => {
      lastSent = 0;
      navigator.geolocation.getCurrentPosition(
        (pos) => post(pos, true),
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 15000 }
      );
    };

    let wakeLock = null;
    const acquireWakeLock = async () => {
      try {
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch {}
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        pushNow();
        acquireWakeLock();
      }
    };

    pushNow();
    acquireWakeLock();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("pageshow", onVisible);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onVisible);
      if (wakeLock) wakeLock.release().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, user]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      if (!["mover", "admin"].includes(data.role)) {
        setError("This login is for movers only.");
      } else {
        setUser(data);
        loadStatus();
      }
    } catch {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="h-8 w-8 animate-spin text-blaze" /></div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="mover-login-page">
        <Loader2 className="h-8 w-8 animate-spin text-blaze" />
      </div>
    );
  }

  if (user.role === "mover" && onboard === null) {
    return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="h-8 w-8 animate-spin text-blaze" /></div>;
  }

  const ob = onboard || { status: "active", prefill: {}, profile: {} };

  if (showOnboarding) {
    return (
      <Onboarding
        user={user}
        status={ob}
        onComplete={() => { setShowOnboarding(false); loadStatus(); }}
        onLogout={logout}
      />
    );
  }

  if (ob.status === "locked") {
    return <LockScreen onStart={() => setShowOnboarding(true)} onLogout={logout} graceUntil={ob.grace_until} />;
  }

  const todayStr = new Date().toLocaleDateString("en-CA");
  const tomorrowStr = new Date(Date.now() + 86400000).toLocaleDateString("en-CA");  const todayJobs = jobs.filter((j) => j.moving_date === todayStr && j.status !== "completed");
  const tomorrowJobs = jobs.filter((j) => j.moving_date === tomorrowStr && j.status !== "completed");
  const upcomingJobs = jobs
    .filter((j) => j.status !== "completed" && j.moving_date !== todayStr && j.moving_date !== tomorrowStr)
    .sort((a, b) => (a.moving_date > b.moving_date ? 1 : -1));
  const completedJobs = jobs.filter((j) => j.status === "completed").sort((a, b) => (b.moving_date > a.moving_date ? 1 : -1));
  const newJobs = jobs.filter(isNew);
  const activeCount = jobs.filter((j) => j.status !== "completed").length;

  const CardList = ({ list, empty, testid }) => (
    <div className="space-y-4" data-testid={testid}>
      {list.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-line bg-white/60 p-6 text-center text-sm text-slate-500">{empty}</p>
      ) : (
        list.map((j) => <JobCard key={j.job_id} job={j} selfName={user.name} rate={user.pay_rate} onStatus={loadJobs} />)
      )}
    </div>
  );

  // In-progress jobs show the full breakdown; the rest collapse into a dropdown.
  const SmartList = ({ list, empty, testid }) => (
    <div className="space-y-3" data-testid={testid}>
      {list.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-line bg-white/60 p-6 text-center text-sm text-slate-500">{empty}</p>
      ) : (
        list.map((j) =>
          (j.my_timer || {}).running ? (
            <JobCard key={j.job_id} job={j} selfName={user.name} rate={user.pay_rate} onStatus={loadJobs} />
          ) : (
            <CollapsibleJob key={j.job_id} job={j} selfName={user.name} rate={user.pay_rate} onStatus={loadJobs} />
          )
        )
      )}
    </div>
  );

  const upcomingSorted = [...todayJobs, ...tomorrowJobs, ...upcomingJobs].sort((a, b) => jobTs(a) - jobTs(b));
  const completedSorted = [...completedJobs].sort((a, b) => jobTs(b) - jobTs(a));
  const dateJobs = selectedDate ? jobs.filter((j) => j.moving_date === selectedDate).sort((a, b) => jobTs(a) - jobTs(b)) : [];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="mover-dashboard">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-200/80 bg-white/80 shadow-sm backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-2xl items-center justify-between px-4">
          <Logo testid="mover-app-logo" iconSize="h-7" textSize="text-sm" />
          <button onClick={() => setNav("profile")} className="transition-transform active:scale-95" data-testid="header-avatar-btn">
            {ob.has_photo ? (
              <img src={`${API}/onboarding/photo/${user.id}`} alt={user.name} className="h-9 w-9 rounded-full border-2 border-white object-cover shadow-sm ring-2 ring-blue-500/30" data-testid="header-avatar-photo" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blaze text-sm font-bold text-white shadow-sm ring-2 ring-blue-500/30">{user.name.charAt(0).toUpperCase()}</span>
            )}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 pb-28 pt-20">
        {nav === "today" && (
          <div data-testid="mover-today-screen">
            <div className="relative overflow-hidden rounded-3xl bg-slate-900 p-5 text-white shadow-[0_12px_40px_rgba(15,23,42,0.35)]">
              <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-blaze/25 blur-3xl" />
              <div className="relative flex items-center gap-4">
                {ob.has_photo ? (
                  <img src={`${API}/onboarding/photo/${user.id}`} alt={user.name} className="h-14 w-14 rounded-2xl border-2 border-slate-700 object-cover shadow-md ring-4 ring-blue-500/20" data-testid="today-avatar-photo" />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blaze font-display text-2xl font-bold text-white ring-4 ring-blue-500/20">{user.name.charAt(0).toUpperCase()}</span>
                )}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-300">
                    {new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}
                  </p>
                  <h1 className="mt-0.5 font-display text-xl font-bold">Hi {user.name.split(" ")[0]} 👋</h1>
                  {ob.worker_role && <p className="text-[10px] font-bold uppercase tracking-widest text-blue-200/80">{ob.worker_role === "driver" ? "Driver" : "Helper / Offsider"}</p>}
                </div>
              </div>
              <div className="relative mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
                  <p className="font-display text-2xl font-bold text-white">{todayJobs.length}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-200">Today</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
                  <p className="font-display text-2xl font-bold text-white">{activeCount}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-200">Active</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-3 backdrop-blur">
                  <p className="font-display text-2xl font-bold text-white">${user.pay_rate}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-200">Per Hour</p>
                </div>
              </div>
            </div>

            <WeekPayCard onOpen={() => setNav("earnings")} />

            <PushEnable />

            {ob.status === "temporary" && !ob.agreement_signed && (
              <button
                onClick={() => setShowOnboarding(true)}
                className="mt-4 w-full rounded-2xl border border-amber-400 bg-amber-50 p-4 text-left text-sm font-semibold text-amber-800 active:scale-[0.99]"
                data-testid="temporary-access-banner"
              >
                Temporary access granted by the office — tap here to complete your profile &amp; sign the agreement before it expires.
              </button>
            )}

            {newJobs.length > 0 && (
              <div className="mt-4 rounded-2xl border border-blaze bg-blue-50 p-4 text-sm font-semibold text-blaze" data-testid="new-jobs-banner">
                {newJobs.length} new job assignment{newJobs.length === 1 ? "" : "s"} — look for the NEW badge.
              </div>
            )}

            <h2 className="mb-3 mt-6 text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Today's Jobs</h2>
            <SmartList list={todayJobs} empty="No jobs today. Enjoy the break!" testid="mover-today" />

            <h2 className="mb-3 mt-8 text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Tomorrow</h2>
            <SmartList list={tomorrowJobs} empty="No jobs tomorrow yet." testid="mover-tomorrow" />
          </div>
        )}

        {nav === "jobs" && (
          <div data-testid="mover-jobs-screen">
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-200 p-1">
              <button
                onClick={() => setJobsView("upcoming")}
                className={`rounded-lg py-3 text-xs font-bold uppercase tracking-widest transition-all ${jobsView === "upcoming" ? "bg-white text-blaze shadow" : "text-slate-500"}`}
                data-testid="jobs-view-upcoming"
              >
                Upcoming ({todayJobs.length + tomorrowJobs.length + upcomingJobs.length})
              </button>
              <button
                onClick={() => setJobsView("completed")}
                className={`rounded-lg py-3 text-xs font-bold uppercase tracking-widest transition-all ${jobsView === "completed" ? "bg-white text-blaze shadow" : "text-slate-500"}`}
                data-testid="jobs-view-completed"
              >
                Completed ({completedJobs.length})
              </button>
            </div>
            {selectedDate ? (
              <div className="mt-4">
                <div className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="date-filter-chip">
                  <p className="text-sm font-bold text-blaze">
                    {fmtDate(selectedDate)} — {dateJobs.length} job{dateJobs.length === 1 ? "" : "s"}
                  </p>
                  <button onClick={() => setSelectedDate(null)} className="text-xs font-bold uppercase tracking-wider text-slate-500 active:text-slate-800" data-testid="clear-date-filter">
                    ✕ Clear
                  </button>
                </div>
                <div className="mt-4 space-y-3" data-testid="mover-datewise">
                  {dateJobs.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-line bg-white/60 p-6 text-center text-sm text-slate-500">No jobs on this date.</p>
                  ) : (
                    dateJobs.map((j) => (
                      <CollapsibleJob key={j.job_id} job={j} selfName={user.name} rate={user.pay_rate} defaultOpen={dateJobs.length === 1} onStatus={loadJobs} />
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-3" data-testid={jobsView === "upcoming" ? "mover-upcoming" : "mover-completed"}>
                {(jobsView === "upcoming" ? upcomingSorted : completedSorted).length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-line bg-white/60 p-6 text-center text-sm text-slate-500">
                    {jobsView === "upcoming" ? "No upcoming jobs assigned." : "No completed jobs yet."}
                  </p>
                ) : (
                  (jobsView === "upcoming" ? upcomingSorted : completedSorted).map((j) => (
                    <CollapsibleJob key={j.job_id} job={j} selfName={user.name} rate={user.pay_rate} onStatus={loadJobs} />
                  ))
                )}
              </div>
            )}
            <MiniCalendar jobs={jobs} selected={selectedDate} onSelect={setSelectedDate} />
          </div>
        )}

        {nav === "mapview" && <MoverMap />}

        {nav === "earnings" && <Earnings user={user} />}

        {nav === "profile" && (
          <div data-testid="mover-profile-screen">
            <div className="rounded-3xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              {ob.has_photo ? (
                <img src={`${API}/onboarding/photo/${user.id}`} alt={user.name} className="mx-auto h-20 w-20 rounded-2xl border-2 border-slate-200 object-cover shadow-md ring-4 ring-blue-500/10" data-testid="profile-photo" />
              ) : (
                <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-blaze font-display text-3xl font-bold text-white shadow-md ring-4 ring-blue-500/10">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              )}
              <h1 className="mt-3 font-display text-xl font-bold text-slate-900">{user.name}</h1>
              <p className="text-sm text-slate-500">{user.email}</p>
              {ob.worker_role && (
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-blaze" data-testid="profile-worker-role">
                  {ob.worker_role === "driver" ? "Driver" : "Helper / Offsider"}
                </p>
              )}
              <span className="mt-3 inline-block rounded-full bg-green-100 px-4 py-1.5 text-xs font-bold text-green-700">${user.pay_rate}/hr pay rate</span>
            </div>

            <div className="mt-4 space-y-3">
              {ob.agreement_signed ? (
                <button onClick={() => setShowAgreement(true)} className="flex w-full items-center gap-3 rounded-2xl border border-line bg-white p-4 text-sm font-semibold text-slate-800 shadow-sm active:scale-[0.98]" data-testid="view-agreement-btn">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50"><FileCheck2 className="h-5 w-5 text-green-600" /></span>
                  View Signed Agreement
                </button>
              ) : (
                <button onClick={() => setShowOnboarding(true)} className="flex w-full items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm font-semibold text-amber-800 shadow-sm active:scale-[0.98]" data-testid="complete-onboarding-btn">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100"><FileCheck2 className="h-5 w-5 text-amber-600" /></span>
                  Complete Profile &amp; Sign Agreement
                </button>
              )}
              <a href="/signoff" className="flex items-center gap-3 rounded-2xl border border-line bg-white p-4 text-sm font-semibold text-slate-800 shadow-sm active:scale-[0.98]" data-testid="mover-signoff-link">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50"><ClipboardSignature className="h-5 w-5 text-blaze" /></span>
                Job Sign-Off Page
              </a>
              <button onClick={() => setNav("earnings")} className="flex w-full items-center gap-3 rounded-2xl border border-line bg-white p-4 text-sm font-semibold text-slate-800 shadow-sm active:scale-[0.98]" data-testid="mover-profile-payslips">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50"><Wallet className="h-5 w-5 text-green-600" /></span>
                My Payslips
              </button>
              <button onClick={() => setNav("jobs")} className="flex w-full items-center gap-3 rounded-2xl border border-line bg-white p-4 text-sm font-semibold text-slate-800 shadow-sm active:scale-[0.98]" data-testid="mover-profile-jobs">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-50"><CalendarDays className="h-5 w-5 text-violet-600" /></span>
                All My Jobs
              </button>
              <button onClick={logout} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 p-4 text-sm font-bold uppercase tracking-widest text-white active:scale-[0.98]" data-testid="mover-logout-btn">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>
        )}
      </main>

      {showAgreement && <AgreementView onClose={() => setShowAgreement(false)} />}

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur-xl" data-testid="mover-bottom-nav">
        <div className="mx-auto grid max-w-2xl grid-cols-5">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setNav(key)}
              className={`flex flex-col items-center gap-1 py-2.5 transition-colors ${nav === key ? "text-blaze" : "text-slate-400"}`}
              data-testid={`mover-nav-${key}`}
            >
              <span className={`flex h-8 w-14 items-center justify-center rounded-full transition-colors ${nav === key ? "bg-blue-50" : ""}`}>
                <Icon className="h-5 w-5" strokeWidth={nav === key ? 2.4 : 2} />
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
